<template>
  <div class="projet-part">
    <h2 id="infos">{{ $t("projetPage.labellisation.suivi.titre") }}</h2>

    <p class="info">{{ $t("projetPage.labellisation.suivi.intro") }}</p>

    <InputWithValidation
      v-model="localPrevuFinAnnee"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      type="textarea"
      :label="
        projetAnnuel.typeDossier !== 'DOSSIER_RENOUVELLEMENT'
          ? $t('projetPage.labellisation.suivi.prevu-fin-annee-label')
          : $t('projetPage.labellisation.suivi.prevu-fin-annee-message-lourd')
      "
      :input-notice="
        projetAnnuel.typeDossier !== 'DOSSIER_RENOUVELLEMENT'
          ? $t('projetPage.labellisation.suivi.prevu-fin-annee-message')
          : ''
      "
      maxlength="1400"
      :disabled="readOnly"
      vid="infos_prevuFinAnnee"
      @manual-change="modified()"
    />

    <GroupCheckboxesWithValidation
      v-model="checkedContexteCheckBoxesStrings"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      rules="maxChoices:2"
      :label="
        projetAnnuel.typeDossier !== 'DOSSIER_RENOUVELLEMENT'
          ? $t('projetPage.labellisation.suivi.entraves')
          : $t('projetPage.labellisation.suivi.entraves-lourd')
      "
      :data="contexteCheckBoxesStrings"
      :disabled="readOnly"
      :vertical-display="true"
      :ext-input-value="localEntravesAutres"
      :input-label="$t('projetPage.labellisation.suivi.problemes.AUTRE')"
      :input-notice="$t('projetPage.labellisation.suivi.entraves-message')"
      vid="infos_entravesAutres"
      @manual-change="changeEntraves()"
      @input-change="changeAutreEntraves"
    />
    <InputWithValidation
      v-model="localElementsFacilitateurs"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      type="textarea"
      :label="
        projetAnnuel.typeDossier !== 'DOSSIER_RENOUVELLEMENT'
          ? $t('projetPage.labellisation.suivi.faciliteurs')
          : $t('projetPage.labellisation.suivi.faciliteurs-lourd')
      "
      maxlength="1400"
      :disabled="readOnly"
      vid="infos_elementsFacilitateurs"
      @manual-change="modified()"
    />
    <InputWithValidation
      v-model="localEvaluationImpactEnvironnementEleves"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      type="textarea"
      :label="$t('projetPage.labellisation.suivi.evaluation-impact')"
      maxlength="1400"
      :disabled="readOnly"
      vid="infos_evaluationImpactEnvironnementEleves"
      @manual-change="modified()"
    />
    <InputWithValidation
      v-model="localImpactSurTerritoire"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      type="textarea"
      :label="
        projetAnnuel.typeDossier !== 'DOSSIER_RENOUVELLEMENT'
          ? $t('projetPage.labellisation.suivi.impact')
          : $t('projetPage.labellisation.suivi.prevu-fin-annee-label-lourd')
      "
      :input-notice="
        projetAnnuel.typeDossier !== 'DOSSIER_RENOUVELLEMENT'
          ? $t('projetPage.labellisation.suivi.impact-message')
          : ''
      "
      maxlength="1400"
      :disabled="readOnly"
      vid="infos_impactSurTerritoire"
      @manual-change="modified()"
    />

    <InputWithValidation
      v-model="localCommentaireSupplementaire"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      type="textarea"
      :label="$t('projetPage.labellisation.suivi.commentaire')"
      maxlength="1400"
      :disabled="readOnly"
      vid="infos_commentaireSupplementaire"
      @manual-change="modified()"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import GroupCheckboxesWithValidation from "@/views/inputs/GroupCheckBoxesWithValidation.vue"
import { t } from "@/utils/i18n-utils"

const CONTEXTE_STRINGS = {
  NON: t("projetPage.labellisation.suivi.problemes.AUCUNE"),
  FINANCEMENT: t("projetPage.labellisation.suivi.problemes.FINANCEMENT"),
  METEO: t("projetPage.labellisation.suivi.problemes.ANNULATION_METEO"),
  MAIRIE: t("projetPage.labellisation.suivi.problemes.DIFFICULTES_MAIRIE"),
  USAGERS: t("projetPage.labellisation.suivi.problemes.TENSION_USAGERS_AIRE"),
  ADMINISTRATIF: t("projetPage.labellisation.suivi.problemes.DIFFICULTES_ADMINISTRATIVES"),
  ABSENCES: t("projetPage.labellisation.suivi.problemes.ABSENCE_LONG_TERME"),
  AMPLEUR: t("projetPage.labellisation.suivi.problemes.MAUVAISE_ESTIMATION"),
  AUTRE: t("projetPage.labellisation.suivi.problemes.AUTRE"),
}

export default defineComponent({
  name: "SuiviView",
  components: {
    InputWithValidation,
    GroupCheckboxesWithValidation,
  },
  props: {
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: false,
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["projet-annuel-updated", "dirtify-project", "trigger-validation"],
  data() {
    return {
      contexteCheckBoxesStrings: Object.values(CONTEXTE_STRINGS).filter(
        (value) => value !== CONTEXTE_STRINGS.AUTRE,
      ),
      checkedContexteCheckBoxesStrings: [] as string[],
    }
  },
  computed: {
    localPrevuFinAnnee: {
      get(): string {
        return this.projetAnnuel.prevuFinAnnee || ""
      },
      set(value: string): void {
        this.updateProjetAnnuel("prevuFinAnnee", value)
      },
    },
    localElementsFacilitateurs: {
      get(): string {
        return this.projetAnnuel.elementsFacilitateurs || ""
      },
      set(value: string): void {
        this.updateProjetAnnuel("elementsFacilitateurs", value)
      },
    },
    localEvaluationImpactEnvironnementEleves: {
      get(): string {
        return this.projetAnnuel.evaluationImpactEnvironnementEleves || ""
      },
      set(value: string): void {
        this.updateProjetAnnuel("evaluationImpactEnvironnementEleves", value)
      },
    },
    localImpactSurTerritoire: {
      get(): string {
        return this.projetAnnuel.impactSurTerritoire || ""
      },
      set(value: string): void {
        this.updateProjetAnnuel("impactSurTerritoire", value)
      },
    },
    localCommentaireSupplementaire: {
      get(): string {
        return this.projetAnnuel.commentaireSupplementaire || ""
      },
      set(value: string): void {
        this.updateProjetAnnuel("commentaireSupplementaire", value)
      },
    },
    localEntravesAutres: {
      get(): string {
        return this.projetAnnuel.entravesAutres || ""
      },
      set(value: string): void {
        this.updateProjetAnnuel("entravesAutres", value)
      },
    },
  },
  watch: {
    projetAnnuel() {
      this.dossierChanged()
    },
  },
  created(): void {
    this.dossierChanged()
  },
  methods: {
    dossierChanged(): void {
      this.checkedContexteCheckBoxesStrings = this.getCheckedContexteCheckBoxesStrings()
    },

    modified(): void {
      this.$emit("dirtify-project")
      this.$emit("trigger-validation")
    },

    updateProjetAnnuel<K extends keyof ProjetAnnuelDTO>(
      attributeName: K,
      value: ProjetAnnuelDTO[K],
    ): void {
      const updatedProjet = { ...this.projetAnnuel }
      updatedProjet[attributeName] = value
      this.$emit("projet-annuel-updated", updatedProjet)
    },

    changeEntraves(): void {
      // Créer une copie pour éviter la mutation de prop
      const updatedProjet = { ...this.projetAnnuel }
      updatedProjet.entraves = []

      if (this.checkedContexteCheckBoxesStrings.includes(CONTEXTE_STRINGS.NON)) {
        updatedProjet.entraves.push("AUCUNE")
      }

      if (this.checkedContexteCheckBoxesStrings.includes(CONTEXTE_STRINGS.FINANCEMENT)) {
        updatedProjet.entraves.push("FINANCEMENT")
      }
      if (this.checkedContexteCheckBoxesStrings.includes(CONTEXTE_STRINGS.METEO)) {
        updatedProjet.entraves.push("ANNULATION_METEO")
      }
      if (this.checkedContexteCheckBoxesStrings.includes(CONTEXTE_STRINGS.MAIRIE)) {
        updatedProjet.entraves.push("DIFFICULTES_MAIRIE")
      }
      if (this.checkedContexteCheckBoxesStrings.includes(CONTEXTE_STRINGS.USAGERS)) {
        updatedProjet.entraves.push("TENSION_USAGERS_AIRE")
      }
      if (this.checkedContexteCheckBoxesStrings.includes(CONTEXTE_STRINGS.ADMINISTRATIF)) {
        updatedProjet.entraves.push("DIFFICULTES_ADMINISTRATIVES")
      }
      if (this.checkedContexteCheckBoxesStrings.includes(CONTEXTE_STRINGS.ABSENCES)) {
        updatedProjet.entraves.push("ABSENCE_LONG_TERME")
      }
      if (this.checkedContexteCheckBoxesStrings.includes(CONTEXTE_STRINGS.AMPLEUR)) {
        updatedProjet.entraves.push("MAUVAISE_ESTIMATION")
      }
      if (this.checkedContexteCheckBoxesStrings.includes(CONTEXTE_STRINGS.AUTRE)) {
        updatedProjet.entraves.push("AUTRE")
      }

      // Émettre la mise à jour au parent
      this.$emit("projet-annuel-updated", updatedProjet)
      this.modified()
    },

    getCheckedContexteCheckBoxesStrings(): string[] {
      const contexteString: string[] = []
      if (this.projetAnnuel.entraves) {
        if (this.projetAnnuel.entraves.includes("AUCUNE")) {
          contexteString.push(CONTEXTE_STRINGS.NON)
        }
        if (this.projetAnnuel.entraves.includes("FINANCEMENT")) {
          contexteString.push(CONTEXTE_STRINGS.FINANCEMENT)
        }
        if (this.projetAnnuel.entraves.includes("ANNULATION_METEO")) {
          contexteString.push(CONTEXTE_STRINGS.METEO)
        }
        if (this.projetAnnuel.entraves.includes("DIFFICULTES_MAIRIE")) {
          contexteString.push(CONTEXTE_STRINGS.MAIRIE)
        }
        if (this.projetAnnuel.entraves.includes("TENSION_USAGERS_AIRE")) {
          contexteString.push(CONTEXTE_STRINGS.USAGERS)
        }
        if (this.projetAnnuel.entraves.includes("DIFFICULTES_ADMINISTRATIVES")) {
          contexteString.push(CONTEXTE_STRINGS.ADMINISTRATIF)
        }
        if (this.projetAnnuel.entraves.includes("ABSENCE_LONG_TERME")) {
          contexteString.push(CONTEXTE_STRINGS.ABSENCES)
        }
        if (this.projetAnnuel.entraves.includes("MAUVAISE_ESTIMATION")) {
          contexteString.push(CONTEXTE_STRINGS.AMPLEUR)
        }
        if (this.projetAnnuel.entraves.includes("AUTRE")) {
          contexteString.push(CONTEXTE_STRINGS.AUTRE)
        }
      }
      return contexteString
    },

    changeAutreEntraves(value: string): void {
      // Utiliser le setter du computed property au lieu de muter directement
      this.localEntravesAutres = value
      this.modified()
    },
  },
})
</script>

<style lang="scss" scoped>
.projet-part h2 {
  padding-bottom: 0;
}
</style>
